import React, { useMemo } from "react";
import { schema } from "../schema";

/**
 * ZoneMapSVG — SVG zone map with alert state coloring.
 *
 * Props:
 *   zones: Array<{ id, zoneName, svgPoints? }>
 *   alarms: Array   — from Firestore (GroupedAlarms)
 *   onZoneClick: (alarm) => void  — opens modal for that zone's alarm
 *
 * If no zone has svgPoints defined this component renders nothing.
 */

function polyCenter(pts) {
  const p = pts.split(" ").map(s => { const [x, y] = s.split(",").map(Number); return { x, y }; });
  return { x: p.reduce((s, v) => s + v.x, 0) / p.length, y: p.reduce((s, v) => s + v.y, 0) / p.length };
}

function ago(ms) {
  if (!ms) return "--";
  const d = Date.now() - ms;
  if (d < 60000)    return `${Math.floor(d / 1000)}s`;
  if (d < 3600000)  return `${Math.floor(d / 60000)}min`;
  if (d < 86400000) return `${Math.floor(d / 3600000)}h`;
  return `${Math.floor(d / 86400000)}d`;
}

export default function ZoneMapSVG({ zones = [], alarms = [], onZoneClick }) {
  // Must call Hooks before any early return
  const svgZones = zones.filter(z => z.svgPoints);

  // Build a map of zoneID → activeAlarm
  const activeByZone = useMemo(() => {
    const m = {};
    alarms
      .filter(a => !a.resolvedAt)
      .forEach(a => {
        const zid = String(a[schema.zoneField] || "");
        if (!m[zid]) m[zid] = a;
      });
    return m;
  }, [alarms]);

  if (svgZones.length === 0) return null;

  return (
    <div style={{
      background: "var(--card)", border: "1px solid var(--border)",
      borderRadius: 10, overflow: "hidden", marginBottom: 10,
    }}>
      <svg viewBox="0 0 660 520" style={{ width: "100%", display: "block" }}>
        <defs>
          <pattern id="svgGrid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0L0 0 0 40" fill="none" stroke="#1e293b" strokeWidth=".5" />
          </pattern>
          <filter id="svgGlow">
            <feGaussianBlur stdDeviation="5" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <rect width="660" height="520" fill="#0f172a" />
        <rect width="660" height="520" fill="url(#svgGrid)" />

        {svgZones.map(z => {
          const alarm = activeByZone[String(z.id)];
          const isActive = alarm && !alarm.resolvedAt;

          // Determine visual state based on alarm phase
          const isDispatched = alarm?.dispatchedBy && !alarm?.resolvedAt;
          const fill  = isActive ? (isDispatched ? "#f59e0b" : "#dc2626") : "#64748b";
          const opac  = isActive ? 0.7 : 0.06;
          const strk  = isActive ? fill : "#334155";
          const ct    = polyCenter(z.svgPoints);

          return (
            <g key={z.id}
               style={{ cursor: isActive ? "pointer" : "default" }}
               onClick={() => isActive && onZoneClick?.(alarm)}>
              <polygon
                points={z.svgPoints}
                fill={fill}
                fillOpacity={opac}
                stroke={strk}
                strokeWidth={isActive ? 2.5 : 1}
                filter={isActive && !isDispatched ? "url(#svgGlow)" : "none"}
              >
                {isActive && !isDispatched && (
                  <animate attributeName="fill-opacity" values=".45;.8;.45" dur="2s" repeatCount="indefinite" />
                )}
              </polygon>
              {/* Zone label */}
              <text
                x={ct.x} y={ct.y - (isActive ? 10 : 0)}
                textAnchor="middle"
                fill={isActive ? "#fff" : "#4b5563"}
                fontSize={isActive ? "14" : "13"}
                fontWeight="700"
                fontFamily="Inter,sans-serif"
                style={{ pointerEvents: "none" }}
              >{z.zoneName}</text>
              {/* Status sub-label */}
              {isActive && (
                <text
                  x={ct.x} y={ct.y + 10}
                  textAnchor="middle"
                  fill={isDispatched ? "#fbbf24" : "#fca5a5"}
                  fontSize="9" fontWeight="600"
                  fontFamily="monospace"
                  style={{ pointerEvents: "none" }}
                >
                  {isDispatched ? `Despachado · ${ago(alarm.dispatchTime)}` : `ACTIVO`}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
